/* eslint-disable no-nested-ternary */
import { Button, Grid } from '@material-ui/core';
import { Autocomplete, TextFieldPx, FormikTextFieldPx, FormikAutocompletePx, useMutation, useToast, useAuth, useQuery } from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { useEffect, useState, useRef } from 'react';
import { getBusinessEmployer, updateBusinessEmployer, getAddress } from '@webapps/business';
import { useFormik } from 'formik';
import { EMPLOYER_TYPE, SPECIFIC_EMPLOYER, OPCOS } from 'config/constants';
import PhoneInput, {
  isValidPhoneNumber,
} from 'react-phone-number-input';
import { FormattedMessage } from 'react-intl';

const BusinessContract = () => {
  const {
    classes,
    cx,
    css,
    theme: { spacing, fontSizes },
  } = useStyles(styles);

  const [addresses, setAddresses] = useState([]);
  const [phone, setPhone] = useState("");
  const [errorPhone, setErrorPhone] = useState(false);
  const [phoneClasses, setPhoneClasses] = useState('phoneInput');
  const phoneClassesRef = useRef();
  phoneClassesRef.current = phoneClasses;

  const { addToast } = useToast();
  const { idUser } = useAuth();

  const { data: infosContract = {}, refetch: refetchInfosContract } = useQuery(
    getBusinessEmployer,
    {
      key: ['infosContract', idUser],
      variables: { id: idUser },
    }
  );

  const {
    isSuccess,
    isError,
    mutateAsync: mutateUpdateBusinessEmployer,
  } = useMutation(updateBusinessEmployer);

  const getAddressInfo = useMutation(getAddress, {
    onSuccess: (result) => {
      setAddresses(result?.data?.features);

      const firstAddress = result?.data?.features[0]?.properties;
      formik.setFieldValue('city', firstAddress?.city);
      formik.setFieldValue('cityCode', firstAddress?.postcode);
    },
  });

  const handleChangeAddress = async (searchValue) => {
    if (!searchValue.target.value || searchValue.target.value.length < 3) {
      return null;
    }
    await getAddressInfo.mutateAsync({ name: searchValue.target.value });
  };

  const handleChangePhone = (phoneInfo) => {
    if (phoneInfo) {
      setPhone(phoneInfo);
      if (isValidPhoneNumber(phoneInfo)) {
        setErrorPhone(false);
        setPhoneClasses('phoneInput');
        formik.setFieldValue('phone', phoneInfo);
      } else {
        setErrorPhone(true);
        setPhoneClasses('phoneInputError');
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      firstname: infosContract?.firstname || '',
      lastname: infosContract?.lastname || '',
      email: infosContract?.email || '',
      phone: infosContract?.phone || '',
      address: infosContract?.address || '',
      city: infosContract?.city || '',
      cityCode: infosContract?.cityCode || '',
      number: infosContract?.number || '',
      street: infosContract?.street || '',
      type: infosContract?.type || '',
      specialization: infosContract?.specialization || '',
      collectiveConvention: infosContract?.collectiveConvention || '',
      addressDetails: infosContract?.addressDetails || '',
      codeIDCC: infosContract?.codeIDCC || '',
      codeNAF: infosContract?.codeNAF || '',
      opco: infosContract?.opco || '',
      totalEmployees: infosContract?.totalEmployees || 0,
      pensionFund: infosContract?.pensionFund || '',
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      await mutateUpdateBusinessEmployer({ id: idUser, data: {...values} });
    },
  });

  useEffect(() => {
    if (isSuccess) {
      addToast({
        status: 'success',
        message: 'Informations mises à jour avec succès',
      });
      refetchInfosContract();
    } else if (isError) {
      addToast({
        status: 'error',
        message: 'Une erreur est survenue',
      });
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    if(infosContract?.address)
      formik.setFieldValue('address', infosContract?.address);
    if (infosContract?.phone) {
      setPhone(infosContract?.phone);
    }
  }, [infosContract]);

  return (
    <Grid spacing={3} xs={12} className={classes.container}>
      <Grid item md={12} className={classes.card}>
        <div className={classes.title}>Informations des données requises lors de la signature d'un contrat</div>
        <Grid container xs={12} className={classes.btnsContainer2}>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikAutocompletePx
              formik={formik}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              onChange={(event, value) => {
                formik.setFieldValue('type', value?.value);
              }}
              label="Type d'employeur"
              name="type"
              options={EMPLOYER_TYPE}
              value={EMPLOYER_TYPE.find((option) => option?.value === formik.values.type) || ''}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikAutocompletePx
              formik={formik}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              onChange={(event, value) => {
                formik.setFieldValue('specialization', value?.value);
              }}
              label="Employeur spécifique"
              name="specialization"
              options={SPECIFIC_EMPLOYER}
              value={SPECIFIC_EMPLOYER.find((option) => option?.value === formik.values.specialization) || ''}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Code NAF"
              name="codeNAF"
              value={formik.values.codeNAF}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikAutocompletePx
              formik={formik}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              label="Choisir un OPCO"
              name="opco"
              onChange={(event, value) => {
                formik.setFieldValue('opco', value?.label);
              }}
              options={OPCOS}
              value={OPCOS.find((opco) => opco.label === formik.values.opco) || ''}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Effectif total"
              name="totalEmployees"
              type="number"
              value={formik.values.totalEmployees}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Code IDCC"
              name="codeIDCC"
              value={formik.values.codeIDCC}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Convention collective"
              name="collectiveConvention"
              value={formik.values.collectiveConvention}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Caisse de retraite complémentaire"
              name="pensionFund"
              value={formik.values.pensionFund}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Nom de l'employeur"
              name="lastname"
              value={formik.values.lastname}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Prénom de l'employeur"
              name="firstname"
              value={formik.values.firstname}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Email de l'employeur"
              name="email"
              value={formik.values.email}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <Autocomplete
              autoComplete={false}
              classes={{
                inputRoot: css({
                  minHeight: "50px !important",
                }),
              }}
              defaultValue=""
              getOptionSelected={(option, value) => option === value}
              inputValue={formik.values.address}
              onInputChange={(event, value) => {
                formik.setFieldValue('address', value);
                handleChangeAddress({ target: { value } });
              }}
              options={addresses?.map((option) => option?.properties?.label)}
              renderInput={(params) => (
                <TextFieldPx
                  {...params}
                  label="Adresse de l'employeur"
                  name="address"
                  onChange={(event) => {
                    formik.setFieldValue('address', event.target.value);
                    handleChangeAddress({ target: { value: event.target.value } });
                  }}
                  value={formik.values.address}
                  variant="outlined"
                />
              )}
              size="small"
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Complément d'adresse (ex: étage, porte...)"
              name="addressDetails"
              value={formik.values.addressDetails}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="N° de rue"
              name="number"
              value={formik.values.number}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <FormikTextFieldPx
              formik={formik}
              fullWidth
              label="Voie"
              name="street"
              value={formik.values.street}
            />
          </Grid>
          <Grid item xs={3} className={cx(classes.textfield, classes.textfieldName)}>
            <PhoneInput
              className={`${classes[phoneClassesRef.current]}`}
              countryCallingCodeEditable={false}
              defaultCountry="FR"
              international
              limitMaxLength
              name="phone"
              onChange={(phone) => {
                handleChangePhone(phone);
              }}
              placeholder={<FormattedMessage id="createUserForm.phone"/>}
              value={phone}
            />
          </Grid>
          <Button
            className={classes.btnDelete}
            onClick={formik.handleSubmit}
            variant="contained"
          >
            Enregistrer
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const styles = ({ fontSizes, spacing }) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '10px',
    padding: spacing(2.5),
  },
  title: {
    fontWeight: 500,
    fontSize: fontSizes(18),
    color: '#152652',
    marginBottom: spacing(2.5),
    fontFamily: 'Rubik',
  },
  label: {
    //    fontSize: fontSizes(16),
    fontSize: fontSizes(16),
    fontWeight: 600,
    color: '#152652',
    '@media (max-width:1380px)': { fontSize: fontSizes(15) },
    '@media (max-width:1278px)': { fontSize: fontSizes(14), },
    '@media (max-width:1227px)': { fontSize: fontSizes(13), },
    '@media (max-width:1020px)': { fontSize: fontSizes(16), },
  },
  password: {
    marginLeft: spacing(2.5),
    marginBottom: spacing(2.5),
  },
  textfield: {
    marginBottom: spacing(2.5),
    flexBasis: 'auto',
  },
  textfieldName: {
    width: '49%',
  },
  infoBox: {
    padding: spacing(4, 2.5, 2.5, 1.5),
    borderRadius: '5px',
    color: '#152652',
    "-webkit-text-fill-color" : "#152652",
    fontSize: fontSizes(16),
    fontFamily: 'Rubik',
    height: '30px',
    backgroundColor: '#F8F8F8',
    display: 'flex',
    alignItems: 'center',
  },
  infoBoxDisabled: {
    borderRadius: '5px',
    color: '#959DB1',
    backgroundColor: '#FFFFFF',
  },
  btnCancel : {
    marginLeft: spacing(2),
    backgroundColor: 'crimson',
    '&:hover' : {
      backgroundColor: 'red',
    },
  },
  btnDelete: {
    marginTop: spacing(2.5),
    float: 'right',
    backgroundColor: 'crimson',
    '&:hover' : {
      backgroundColor: 'red',
    },
    '@media only screen and (max-width:506px)': { width: '100%', flexBasis: '100%' },
  },
  deleteAccount: {
    textAlign: 'center',
    marginBottom: '16px',
    width: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  btnLogout: {
    marginTop: spacing(2.5),
    backgroundColor: 'white',
    '@media only screen and (max-width:506px)': { width: '100%', flexBasis: '100%' },
  },
  btnLogoutContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'self-end',
    flexWrap: 'wrap',
  },
  btnsContainer2: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  phoneInput: {
    height: '100%',
    '& > *': {
      backgroundColor: '#F8F8F8',
      border: "2px solid #E9E9F0",
      borderRadius: 5,
      padding: spacing(4, 2),
      height: '100%',
      color: '#737373',
    },
  },
  phoneInputError: {
    height: '100%',
    '& > *': {
      backgroundColor: '#F8F8F8',
      border: '2px solid red',
      borderRadius: 5,
      padding: spacing(4, 2),
      height: '100%',
      color: '#f44336',
    },
  },
  phoneError: {
    display: 'flex',
    width: '100%',
    '& > *': {
      width: '50%',
    },
  },
  phoneDisabled: {
    '& > *': {
      backgroundColor: 'white',
    },
  }
});

export default BusinessContract;
